// Third-party packages
import React, { useState, useContext, useEffect } from 'react'
import { useHistory, Link } from 'react-router-dom'
import {
  Button,
  Container,
  Form,
  Grid,
  Header,
  Icon,
  Message,
  Segment,
} from 'semantic-ui-react'

// Main packages
import * as api from '../api'
import { AppContext } from './app_context'
import SectionFooter from './section_footer'
import SectionHeader from './section_header'
import SectionTitle from './section_title'

export const PageLogin = () => {
  const history = useHistory()
  const { setLoading, refreshCart } = useContext(AppContext)
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Check if user is already authenticated
  useEffect(() => {
    if (api.isAuthenticated()) {
      // User is already logged in, redirect to intended page or home
      const from = new URLSearchParams(window.location.search).get('from') || '/'
      history.replace(from)
    }
  }, [history])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    setLoading(true, 'login')
    
    // Start user action for login attempt
    let userAction = null;
    if (window.faroTracker && typeof window.faroTracker.startUserAction === 'function') {
      userAction = window.faroTracker.startUserAction('login-attempt', {
        email: formData.email.toLowerCase().trim(),
        login_method: 'email_password'
      }, {
        triggerName: 'form_submit',
        importance: 'high'
      });
    }
    
    try {
      const startTime = Date.now()
      
      const response = await api.customerLogin({
        email: formData.email.toLowerCase().trim(),
        password: formData.password
      })
      
      const duration = Date.now() - startTime
      
      if (response.status === 200 && response.data.message === 'success') {
        const { customer, token } = response.data
        
        // Store authentication data
        localStorage.setItem('auth_token', token)
        localStorage.setItem('customer_id', customer.id)
        localStorage.setItem('customer_email', customer.email)
        localStorage.setItem('customer_name', `${customer.first_name} ${customer.last_name}`)
        
        // Track successful login
        if (window.faroTracker) {
          window.faroTracker.trackEvent('customer_login_success', {
            customer_id: customer.id,
            email: customer.email,
            duration,
            login_method: 'email_password'
          });
          
          // End user action with success
          if (userAction && typeof userAction.end === 'function') {
            userAction.end({ success: true, customer_id: customer.id });
          }
        }
        
        // Refresh cart with customer context
        await refreshCart(true)
        
        // Redirect to home page
        history.replace('/')
        
      } else {
        // Handle login failure
        const errorMessage = response.data?.error || 'Login failed. Please check your credentials.'
        setErrors({ general: errorMessage })
        
        if (window.faroTracker) {
          window.faroTracker.trackEvent('customer_login_failure', {
            error: errorMessage,
            email: formData.email,
            duration,
            failure_reason: 'invalid_credentials'
          });
          
          // End user action with failure
          if (userAction && typeof userAction.end === 'function') {
            userAction.end({ success: false, error: errorMessage });
          }
        }
      }
    } catch (error) {
      console.error('Login error:', error)
      let errorMessage = 'An unexpected error occurred. Please try again.'
      
      if (error.response?.status === 401) {
        errorMessage = 'Invalid email or password. Please try again.'
      } else if (error.response?.status === 429) {
        errorMessage = 'Too many login attempts. Please try again later.'
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error
      }
      
      setErrors({ general: errorMessage })
      
      if (window.faroTracker) {
        window.faroTracker.trackEvent('customer_login_error', {
          error: errorMessage,
          email: formData.email,
          error_type: error.response?.status || 'network_error'
        })
      }
    } finally {
      setIsSubmitting(false)
      setLoading(false, 'login')
    }
  }

  return (
    <>
      <SectionHeader />
      <SectionTitle title="Customer Login" subtitle="Sign in to your BitGeeks Coffee account" />
      
      <Container style={{ paddingTop: '2em', paddingBottom: '4em' }}>
        <Grid centered>
          <Grid.Column mobile={16} tablet={10} computer={6}>
            <Segment raised>
              <Header as="h2" color="brown" textAlign="center">
                <Icon name="user circle" />
                Sign In to Your Account
              </Header>
              
              {errors.general && (
                <Message negative>
                  <Message.Header>Login Failed</Message.Header>
                  <p>{errors.general}</p>
                </Message>
              )}
              
              <Form onSubmit={handleSubmit} loading={isSubmitting}>
                <Form.Field error={!!errors.email}>
                  <label>Email Address</label>
                  <Form.Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email address"
                    icon="mail"
                    iconPosition="left"
                    autoComplete="email"
                    autoFocus
                  />
                  {errors.email && (
                    <div style={{ color: '#9f3a38', fontSize: '0.9em', marginTop: '0.25em' }}>
                      {errors.email}
                    </div>
                  )}
                </Form.Field>
                
                <Form.Field error={!!errors.password}>
                  <label>Password</label>
                  <Form.Input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Enter your password"
                    icon="lock"
                    iconPosition="left"
                    autoComplete="current-password"
                  />
                  {errors.password && (
                    <div style={{ color: '#9f3a38', fontSize: '0.9em', marginTop: '0.25em' }}>
                      {errors.password}
                    </div>
                  )}
                </Form.Field>
                
                <Button 
                  type="submit" 
                  color="blue" 
                  fluid 
                  size="large"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  <Icon name="sign in" />
                  Sign In
                </Button>
              </Form>
              
              <Segment secondary style={{ marginTop: '2em' }}>
                <Grid stackable>
                  <Grid.Column textAlign="center">
                    <p>
                      Don't have an account?{' '}
                      <Link to="/register" style={{ color: '#2185d0', fontWeight: 'bold' }}>
                        Create one here
                      </Link>
                    </p>
                  </Grid.Column>
                </Grid>
              </Segment>
              
              <Message info style={{ marginTop: '1em' }}>
                <Message.Header>Demo Credentials</Message.Header>
                <p>Email: <strong>demo@grafana.com</strong></p>
                <p>Password: <strong>Password123</strong></p>
                <p style={{ fontSize: '0.9em', color: '#666' }}>Or register a new account!</p>
              </Message>
            </Segment>
          </Grid.Column>
        </Grid>
      </Container>
      
      <SectionFooter />
    </>
  )
}
